import { useEffect, useState } from "react";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import { api, deriveKey } from "../sync/api";
import * as local from "../sync/localdb";
import { Button, Tape, useToast } from "../ui/kit";
import { importGuestBoards } from "./guest";
import { previewFor, templateByKey } from "../data/templates";

type Mode = "login" | "signup" | "recover";

function next(): string {
  const n = new URLSearchParams(location.search).get("next");
  return n && n.startsWith("/") && !n.startsWith("//") ? n : "/";
}

export function RecoveryKeyCard({ recoveryKey, onDone, doneLabel = "I saved it — continue" }: { recoveryKey: string; onDone: () => void; doneLabel?: string }) {
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  return (
    <div className="recovery">
      <p>
        This is your <strong>recovery key</strong>. Sepia doesn’t send e-mails, so it’s the only way back in if you forget your password.
        Keep it somewhere safe — it’s shown only once.
      </p>
      <code className="recovery-key">{recoveryKey}</code>
      <div className="row-gap">
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(recoveryKey);
              toast("Recovery key copied");
              setSaved(true);
            } catch {
              toast("Select the key and copy it by hand.", "error");
            }
          }}
        >
          Copy
        </Button>
        <Button
          onClick={() => {
            const blob = new Blob([`Sepia recovery key\n\n${recoveryKey}\n\nUse it at ${location.origin}/recover if you forget your password.\n`], { type: "text/plain" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = "sepia-recovery-key.txt";
            a.click();
            setSaved(true);
          }}
        >
          Save as file
        </Button>
      </div>
      <label className="check-row">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>I’ve kept my recovery key somewhere safe</span>
      </label>
      <Button kind="primary" disabled={!saved} onClick={onDone}>
        {doneLabel}
      </Button>
    </div>
  );
}

export function AuthPage({ mode }: { mode: Mode }) {
  const { user, setUser } = useSession();
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [recovery, setRecovery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [guestCount, setGuestCount] = useState(0);
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    document.title = (mode === "login" ? "Log in" : mode === "signup" ? "Sign up" : "Recover account") + " · Sepia";
    local.listGuestBoards().then((b) => setGuestCount(b.length));
  }, [mode]);

  useEffect(() => {
    if (user && !newKey && !importing) navigate(next(), { replace: true });
  }, [user, newKey, importing]);

  const afterAccount = async () => {
    if (guestCount > 0) {
      setImporting("Bringing your boards over…");
      try {
        const n = await importGuestBoards((d, t) => setImporting(`Bringing your boards over… ${d}/${t}`));
        if (n) toast(`Moved ${n} board${n === 1 ? "" : "s"} into your account`);
      } catch (e) {
        toast("Some boards couldn't be moved: " + (e as Error).message, "error");
      }
      setImporting(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (mode !== "login" && password.length < 8) {
      setErr("Use at least 8 characters for your password.");
      return;
    }
    setBusy(true);
    try {
      const key = await deriveKey(password, email);
      if (mode === "login") {
        const r = await api.login(email, key);
        setUser(r.user);
      } else if (mode === "signup") {
        const r = await api.signup(email, name, key);
        // a first board to explore
        const tour = templateByKey("welcome");
        if (tour && guestCount === 0) {
          const els = tour.make();
          await api.createBoard({ title: "A little tour", bg: tour.bg, els, thumb: await previewFor(els, tour.bg) }).catch(() => undefined);
        }
        setNewKey(r.recoveryKey);
        setUser(r.user);
      } else {
        const r = await api.recover(email, recovery, key);
        setNewKey(r.recoveryKey);
        setUser(r.user);
      }
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <Link to="/welcome" className="brand auth-brand">
        <img src="/favicon-64.png" alt="" width="34" height="34" />
        <span className="wordmark">sepia</span>
      </Link>
      <div className="auth-card">
        <Tape color={mode === "signup" ? "#93ad86" : mode === "login" ? "#d98c8c" : "#d9b25a"} className="auth-tape" />
        {importing ? (
          <div className="recovery">
            <h1>Almost there</h1>
            <p>{importing}</p>
          </div>
        ) : newKey ? (
          <>
            <h1>{mode === "recover" ? "You're back in" : "Welcome to Sepia!"}</h1>
            <RecoveryKeyCard
              recoveryKey={newKey}
              onDone={async () => {
                await afterAccount();
                setNewKey(null);
                navigate(next(), { replace: true });
              }}
            />
          </>
        ) : (
          <form onSubmit={submit} className="auth-form">
            <h1>{mode === "login" ? "Welcome back" : mode === "signup" ? "Make your sketchbook" : "Recover your account"}</h1>
            {mode === "signup" && (
              <label className="field">
                <span>Your name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Ada" maxLength={60} />
              </label>
            )}
            <label className="field">
              <span>E-mail</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" required placeholder="you@example.com" autoCapitalize="none" />
            </label>
            {mode === "recover" && (
              <label className="field">
                <span>Recovery key</span>
                <input value={recovery} onChange={(e) => setRecovery(e.target.value)} required placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" autoCapitalize="characters" autoComplete="off" />
              </label>
            )}
            <label className="field">
              <span>{mode === "recover" ? "New password" : "Password"}</span>
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={mode === "login" ? 1 : 8} placeholder={mode === "login" ? "" : "at least 8 characters"} />
            </label>
            {err && <p className="form-error">{err}</p>}
            {mode === "signup" && guestCount > 0 && <p className="muted small">The {guestCount} board{guestCount === 1 ? "" : "s"} on this device will move into your new account.</p>}
            <Button kind="primary" type="submit" disabled={busy}>
              {busy ? "One moment…" : mode === "login" ? "Log in" : mode === "signup" ? "Create account" : "Reset password"}
            </Button>
            <div className="auth-links">
              {mode === "login" ? (
                <>
                  <Link to={"/signup" + location.search}>New here? Sign up</Link>
                  <Link to="/recover">Forgot password?</Link>
                </>
              ) : (
                <Link to={"/login" + (mode === "signup" ? location.search : "")}>I already have an account</Link>
              )}
            </div>
            {mode === "signup" && <p className="muted small">No e-mails, no tracking. Your password is scrambled on this device before it’s sent.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
